"use client";
import Image from "next/image";
import * as React from "react";
import useInView from "@/lib/use-in-view";

import { cn } from "@/lib/utils";
import { motionConfig } from "@/config/motion";
import { resolveAssetPath, resolveAssetSrcSet } from "@/lib/assets";

// Hook to detect if we're on desktop
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = React.useState(false)

  React.useEffect(() => {
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024) // lg breakpoint
    }

    checkIsDesktop()
    window.addEventListener('resize', checkIsDesktop)
    return () => window.removeEventListener('resize', checkIsDesktop)
  }, [])

  return isDesktop
}

export interface CustomImageProps {
  src: string
  alt: string
  width?: number
  height?: number
  fill?: boolean
  className?: string
  containerClassName?: string
  priority?: boolean
  withBackground?: boolean
  fullWidth?: boolean;
  rounded?: boolean;
  preferredScale?: '1x' | '2x';
  /** Force 1x scale (for tiles) - overrides desktop 2x behavior */
  force1x?: boolean;
}

export function resolveWebpSources(src: string, preferredScale?: '1x' | '2x'): { imageSrc: string; srcSet?: string; sizes?: string } {
  let imageSrc = src;
  let srcSet: string | undefined;
  let sizes: string | undefined;

  // If an explicit .webp or .svg path is provided, preserve it as-is
  // This avoids rewriting known assets like `/assets/.../file.webp`
  if (!/\.(webp|svg)($|\?)/.test(src)) {
    // First, try to match paths that already have scale suffixes (-1x, -2x)
    const scaleMatch = src.match(/^\/(assets|images(?:\/gallery)?)\/(.+?)-(1x|2x)(?:\.webp)?$/);
    if (scaleMatch) {
      const dir = scaleMatch[1];
      const base = scaleMatch[2];
      const scale = scaleMatch[3]; // '1x' | '2x'

      if (preferredScale) {
        imageSrc = `/${dir}/${base}-${preferredScale}.webp`;
      } else {
        imageSrc = `/${dir}/${base}-${scale}.webp`;
        srcSet = `/${dir}/${base}-1x.webp 1x, /${dir}/${base}-2x.webp 2x`;
        sizes = '100vw';
      }
    } else {
      // Handle all other cases by treating the entire path as the base
      const pathMatch = src.match(/^\/(assets|images(?:\/gallery)?)\/(.+)$/);
      if (pathMatch) {
        const dir = pathMatch[1];
        const fullBase = pathMatch[2];

        // Remove any existing .webp extension
        const baseWithoutExt = fullBase.replace(/\.webp$/, '');

        if (preferredScale) {
          imageSrc = `/${dir}/${baseWithoutExt}-${preferredScale}.webp`;
        } else {
          imageSrc = `/${dir}/${baseWithoutExt}-1x.webp`;
          srcSet = `/${dir}/${baseWithoutExt}-1x.webp 1x, /${dir}/${baseWithoutExt}-2x.webp 2x`;
          sizes = '100vw';
        }
      }
    }
  }

  return {
    imageSrc: resolveAssetPath(imageSrc),
    srcSet: resolveAssetSrcSet(srcSet),
    sizes,
  };
}

export function CustomImage({
  src,
  alt,
  width,
  height,
  fill,
  className,
  containerClassName,
  priority,
  withBackground = true,
  fullWidth,
  rounded = true,
  preferredScale,
  force1x = false
}: CustomImageProps) {
  const [ref, inView] = useInView<HTMLDivElement>({ threshold: motionConfig.inViewAmount })
  const [hasBeenInView, setHasBeenInView] = React.useState(false)
  const isDesktop = useIsDesktop()
  const [error, setError] = React.useState(false)

  React.useEffect(() => {
    if (inView && !hasBeenInView) {
      setHasBeenInView(true);
    }
  }, [inView, hasBeenInView]);

  React.useEffect(() => {
    setError(false)
  }, [src])

  // Determine the scale to use
  let effectiveScale = preferredScale
  if (!preferredScale && !force1x) {
    // Auto-scale: 2x on desktop, 1x on mobile
    effectiveScale = isDesktop ? '2x' : '1x'
  } else if (force1x) {
    // Force 1x for tiles
    effectiveScale = '1x'
  }

  // Build responsive sources for both /assets and /images WebP files
  const { imageSrc, srcSet, sizes } = resolveWebpSources(src, effectiveScale);
  const disableNextOptimization = Boolean(srcSet);

  const finalSrc = error ? "https://placehold.co/600x400/png?text=Image+Not+Found" : imageSrc;

  return (
    <div ref={ref} className={containerClassName ?? "w-full lg:w-1/2"}>
      <Image
        loading={priority ? undefined : 'lazy'}
        src={finalSrc}
        alt={alt}
        width={fill ? undefined : width}
        height={fill ? undefined : height}
        fill={fill}
        priority={priority}
        unoptimized={disableNextOptimization || error}
        onError={() => setError(true)}
        className={cn(
          "",
          priority ? '' : 'transition-opacity duration-700',
          rounded ? "rounded-xl" : "",
          withBackground ? "bg-card shadow-md" : "",
          priority ? '' : (hasBeenInView ? "animate-in fade-in" : "opacity-0"),
          fullWidth ? 'w-full' : '',
          className
        )}
        {...(!error && srcSet ? { srcSet } : {})}
        {...(!error && sizes ? { sizes } : {})}
      />
    </div>
  );
}
